import { FC, useEffect, useState } from 'react';
import FilterDropdown, { FilterOption } from '../custom-input/FilterDropdown';
import { getSpecimenOwners, getSpecimensInventory } from '../../api/specimensApi';
import { Specimen } from '../../types/interfaces/specimen.interfaces';
import { Animal } from '../../types/interfaces/animal.interfaces';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { ANIMAL_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';

type SelectSpecimenFromCanisterProps = {
  canisterId?: number;
  selectedSpecimen?: FilterOption<number | undefined>;
  setSelectedSpecimen: (e: FilterOption<number | undefined>) => void;
  selectedOwner: FilterOption<number | undefined>;
  setSelectedOwner: (e: FilterOption<any | undefined>) => void;
};

const SelectSpecimenFromCanister: FC<SelectSpecimenFromCanisterProps> = ({
  canisterId,
  selectedSpecimen,
  setSelectedSpecimen,
  selectedOwner,
  setSelectedOwner,
}) => {
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [owners, setOwners] = useState<FilterOption<number>[]>([]);
  const [specimenOptions, setSpecimenOptions] = useState<FilterOption<number | undefined>[]>([]);
  const [animal, setAnimal] = useState<Animal>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getInventory = async () => {
      if (!canisterId) {
        setSpecimens([]);
        setSelectedSpecimen({ name: '', value: undefined });
        setSelectedOwner({ name: '', value: undefined });
        setAnimal(undefined);
        return;
      }

      setIsLoading(true);

      try {
        const query = { include: 'Animal.Owners', canisterId };
        const res = await getSpecimensInventory(query);
        setSpecimens(res.data);
        setSpecimenOptions(getSpecimenOptions(res.data));
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    getInventory();
  }, [canisterId, setSelectedOwner, setSelectedSpecimen]);

  const handleSpecimen = (specimen: FilterOption<number>) => {
    if (specimen.value === undefined) {
      return;
    }

    setSelectedSpecimen(specimen);
    setSelectedOwner({ name: '', value: undefined });
    const fullSpecimen = specimens.find(s => s.specimenId === specimen.value);
    setAnimal(fullSpecimen?.animal);

    getSpecimenOwners(specimen.value).then(accounts => {
      setOwners(
        accounts.data.map(account => {
          const hasDuePayment =
            account.outstandingBalanceCents != undefined &&
            account.penaltyCents != undefined &&
            (account.outstandingBalanceCents > 0 || account.penaltyCents > 0);
          return {
            name: account.name,
            value: Number(account.accountId),
            optionWarning: hasDuePayment,
            message: hasDuePayment ? 'Due Payment' : '',
          };
        }),
      );
    });
  };

  function getSpecimenOptions(specimensData: any): FilterOption<number | undefined>[] {
    const data: FilterOption<number | undefined>[] = specimensData.map((specimen: Specimen) => {
      return {
        name: `${specimen.animal?.code}-${new Date(specimen.freezeDate).toLocaleDateString()} - ${
          specimen?.lotNumber ? `${specimen.specimenId} (${specimen.lotNumber})` : specimen.specimenId
        }`,
        value: specimen.specimenId,
      };
    });

    return data;
  }

  return (
    <>
      <div className="select-specimen-from-canister">
        <div className="form-row">
          <div className="text">
            <label>{LABEL_CONSTANTS.INVENTORY_ITEMS}:</label>
          </div>
          <div className="input-container">
            <FilterDropdown
              disabled={!canisterId || isLoading}
              placeholder="Choose One"
              searchPlaceholder="Animal Code - Lot Date - Lot Number"
              options={specimenOptions}
              value={selectedSpecimen ?? { name: '', value: '' }}
              onChange={handleSpecimen}
            />
          </div>
        </div>

        <div className="form-row">
          <label>{ANIMAL_CONSTANTS.ANIMAL_NAME}:</label>
          <div className="input-container">
            <label>{canisterId && selectedSpecimen?.value ? animal?.name : '-'}</label>
          </div>
        </div>

        <div className="form-row">
          <div className="text">
            <label>{LABEL_CONSTANTS.ACCOUNT_OWNER}:</label>
          </div>
          <div className="input-container">
            <FilterDropdown
              options={owners}
              value={selectedOwner ?? { name: '', value: undefined }}
              disabled={!animal?.name}
              onChange={setSelectedOwner}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectSpecimenFromCanister;
